<template>
  <div class="request">
    <Modal v-if="showResumeRequestModal" dimmed :width="80" @close-modal="closeModal">
      <h2 v-translate>Do you want to continue where you left off?</h2>
      <p v-translate>
        You previously started a request. If you would like to continue that request, select the
        request below.
      </p>
      <div style="display: flex; flex-flow: column; align-items: center">
        <div class="activity-container" v-for="(activity, i) in startedRequests.list" :key="i">
          <ActivityCard :activity="activity" />
        </div>
        <md-button @click="closeModal" class="button">
          <span v-translate class="inherit-font">Start a New Request</span>
        </md-button>
      </div>
    </Modal>
    <Modal
      v-if="grantLimitModal"
      dimmed
      :additionalContainerStyles="{
        width: '100%',
        overflow: 'hidden',
        'justify-content': 'center',
      }"
      :additionalModalStyles="{
        width: '100%',
        background: '#' + organizationDetails.darkBrandColor,
        color: 'white',
        border: 'none',
        'overflow-y': 'scroll',
        'max-height': '90%',
      }"
    >
      <div class="fcolumn">
        <div class="frow maxedModalSection">
          <div
            class="frow"
            v-if="organizationDetails.grantLimitDuration == 'Lifetime'"
            v-translate="{ grantlimit: grantLimitDollars }"
          >
            Unfortunately, you have already reached the total grant limit of %{grantlimit}.
          </div>
          <div
            class="frow"
            v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
            v-translate="{ grantlimit: grantLimitDollars }"
          >
            Unfortunately, you have already reached the total grant limit of %{grantlimit} in this
            calendar year.
          </div>
          <div
            class="frow"
            v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
            v-translate="{
              grantlimit: grantLimitDollars,
              num: organizationDetails.rollingGrantDuration,
            }"
          >
            Unfortunately, you have already reached the total grant limit of %{grantlimit} in a
            rolling %{num}-year period.
          </div>
        </div>
        <div class="frow maxedModalSection">
          <div
            class="frow"
            v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
            v-translate
          >
            You will be able to request additional grant funds on January 1.
          </div>
          <div
            class="frow"
            v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
            v-translate="{
              date: dateToReApply,
            }"
          >
            You will be able to request additional grant funds on %{date}.
          </div>
        </div>
        <div
          class="frow maxedModalSection"
          v-if="organizationDetails.additionalResourcesCopy"
        >
          <div class="additional-resources" v-html="additionalResources"></div>
        </div>
        <div class="frow maxedModalSection">
          <div v-translate>
            We are sorry you are experiencing financial hardship. We wish you the best.
          </div>
        </div>
        <div class="frow maxedModalSection" style="justify-content: end">
          <router-link
            :to="{ name: 'Home' }"
            style="text-decoration: underline; color: white"
            v-translate
            >Home</router-link
          >
        </div>
      </div>
    </Modal>
    <div class="frow">
      <h1 v-translate>Make a grant request</h1>
    </div>
    <div class="frow">
      <div class="fcolumn">
        <div v-html="hardshipDefinition"></div>
      </div>
    </div>
    <div class="light-brand-color-block frow">
      <div class="fcolumn">
        <p v-translate>To make a grant request, you will need to do the following:</p>
        <ul>
          <li v-translate>Provide payment information so you can receive grant money</li>
          <li v-if="organizationDetails.eventDocumentationEnabled" v-translate>
            RequestPage Describe and document your emergency
          </li>
          <li v-if="!organizationDetails.eventDocumentationEnabled" v-translate>
            RequestPage Describe your emergency
          </li>
          <li
            v-if="
              !organizationDetails.expenseDocumentationEnabled &&
              !organizationDetails.grantRequestAmountEnabled
            "
            v-translate
          >
            RequestPage Describe expenses
          </li>
          <li v-if="organizationDetails.grantRequestAmountEnabled" v-translate>
            RequestPage Describe expenses and amounts needed
          </li>
          <li v-if="organizationDetails.expenseDocumentationEnabled" v-translate>
            Document expenses
          </li>
          <li v-translate>Review and submit your request</li>
        </ul>
      </div>
    </div>
    <div class="frow">
      <p class="strong-text" v-translate>
        You can pause at any point if you need more time to enter details. The information you have
        entered will be saved automatically. You can return to your request when you are ready.
      </p>
    </div>
    <div class="frow">
      <h4 class="heading" v-translate>Your commitment to the Grant Circle</h4>
    </div>
    <div class="frow">
      <div class="certify-box">
        <label class="checkbox-container">
          <input
            type="checkbox"
            id="checkbox"
            :checked="userCertified"
            @change="toggleUserCertified"
          />
          <span class="checkmark"></span>
        </label>
        <label for="checkbox">
          <p v-translate>
            By checking the box below, I certify that all of the information that will be submitted
            in this request is true and correct to the best of my knowledge. I also certify that I
            am requesting funds to overcome a financial emergency.
          </p>
        </label>
      </div>
    </div>
    <div class="frow">
      <md-button
        @click="startRequest"
        class="button"
        :class="{ 'disabled-button': disabled }"
        :disabled="disabled"
      >
        <span v-if="!loading" key="next" v-translate>Get Started</span>
        <span v-else key="spinner">
          <LoadSpinner color="white" />
        </span>
      </md-button>
    </div>
  </div>
</template>

<script>
import { formatDateShort, formatNumberAsUSD } from '@/services/utils'
import Request from '../services/requests'
import { mapState } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import { analyticsMixin } from '@/services/googleAnalytics'
import Action from '../services/actions'
import CollectionManager from '../services/collectionManager'
import ActivityCard from '@/components/myActivity/ActivityCard'
import Modal from '@/components/Modal'
import { customVariablesFilter } from '../services/organizationCustomVariables'
import { translateToCurrentLang, formatText } from '../services/utils'
import UserEvent from '@/services/userEvents/models'
import SurveyResponse from '@/services/surveyresponses'

const REQUEST_STARTED = 'REQUEST_STARTED'
const STARTED = 'STARTED'

export default {
  name: 'Request',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    ActivityCard,
    Modal,
  },
  data() {
    return {
      userCertified: false,
      loading: false,
      startedRequests: CollectionManager.create({
        ModelClass: Action,
        filters: {
          ordering: '-datetime_created',
          actionType: REQUEST_STARTED,
          requestStatus: STARTED,
        },
      }),
      showResumeRequestModal: false,
      grantLimitModal: false,
    }
  },
  async created() {
    if (!this.maxedGrantLimit) {
      this.startedRequests.refresh().then(() => {
        if (this.startedRequests.list.length) {
          // NOTE (Bruno 7-13-20): As per client's request, the modal is tracked as its own page/pageview
          this.trackCustomPageview('ResumeStartedRequest', '/resume-started-request')

          this.showResumeRequestModal = true
        }
      })
    } else if (await this.blockRequest()) {
    }
  },
  methods: {
    closeModal() {
      // NOTE (Bruno 7-14-20): As per client's request, the modal is tracked as its own page/pageview
      this.analyticsEventsTracker(
        'clicked-cta',
        'start a new request',
        'ResumeStartedRequest',
        '/resume-started-request',
      )
      this.showResumeRequestModal = false
    },
    async blockRequest() {
      if (this.maxedGrantLimit) {
        this.grantLimitModal = true
        await UserEvent.api.requestStartBlocked()
        this.trackCustomPageview('RequestStartBlockedMaxedGrantLimit', '/request-start-blocked')
        return true
      }
      return false
    },
    async startRequest() {
      if (await this.blockRequest()) return

      this.loading = true
      this.trackClickedCTA()
      await Request.api
        .create({})
        // Update user's current request in state
        .then((request) => {
          this.$store.dispatch('updateCurrentRequest', request)
        })
        .then(async () => {
          this.$router.push({ name: await this.nextRoute() })
        })
        .finally(() => (this.loading = false))
    },
    async nextRoute() {
      const householdDetailsSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Household Details')
      const finHealthSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Financial Health')

      // if both surveys are complete
      if (householdDetailsSurveyComplete && finHealthSurveyComplete) {
        return 'PayPalVerify'
      }

      // if both surveys are disabled
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        !this.organizationDetails.isFinancialHealthSurveyEnabled
      ) {
        return 'PayPalVerify'
      }

      // if house hold details survey is enabled and complete
      if (
        !this.organizationDetails.isFinancialHealthSurveyEnabled &&
        this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        householdDetailsSurveyComplete
      ) {
        return 'PayPalVerify'
      }

      // if finhealth survey is enabled and complete
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        finHealthSurveyComplete
      ) {
        return 'PayPalVerify'
      }

      // house hold enable and incomplete
      if (this.organizationDetails.isHouseholdDetailsSurveyEnabled && !householdDetailsSurveyComplete) {
        return 'HouseholdDetailsRequest'
      }

      // fin health enabled and incomplete
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        !finHealthSurveyComplete
      ) {
        return 'FinancialHealthRequest'
      }

      return 'HouseholdDetailsRequest'
    },
    toggleUserCertified() {
      if (this.userCertified) {
        this.userCertified = false
      } else {
        this.eventsTracker('certification-of-truth-and-correctness')
        this.userCertified = true
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    grantLimitDollars() {
      return formatNumberAsUSD(this.organizationDetails.grantLimit, true)
    },
    dateToReApply() {
      // Push this logic to backend if we need to use this in multiple places
      var initial_date = new Date(this.user.rollingGrantDate).getTime()
      var final_date = new Date(
        initial_date + this.organizationDetails.rollingGrantDuration * 365 * 24 * 60 * 60 * 1000,
      )
      return formatDateShort(final_date.getTime(), this.$language.current)
    },
    disabled() {
      if (!this.userCertified || this.loading) {
        return true
      }
      return false
    },
    additionalResources() {
      return translateToCurrentLang(
        this.$language.current,
        this.organizationDetails.additionalResourcesCopy.es,
        this.organizationDetails.additionalResourcesCopy.en,
      )
    },
    hardshipDefinition() {
      return formatText(this.$language.current, 
          {
            'en_US': this.organizationDetails.hardshipDefinitionCopy.en,
            'es_ES': this.organizationDetails.hardshipDefinitionCopy.es,
          }, 
          {
            'org': this.organizationDetails,
          }
      )
    },
    maxedGrantLimit() {
      if (!this.organizationDetails.grantLimit) {
        return false
      }
      if (
        this.organizationDetails.grantLimitDuration == 'Lifetime' &&
        this.user.totalPayouts == this.organizationDetails.grantLimit
      ) {
        return true
      }
      if (this.user.totalPayoutsSinceLastGrantLimitReset == this.organizationDetails.grantLimit) {
        return true
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/flex-box';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.maxedModalSection {
  margin: 1rem 0 0 0;
  justify-content: start;
  align-items: center;
}

.request {
  @include screen-container();
  justify-content: unset;
  row-gap: 1rem;
}
.heading {
  margin: 0;
  padding: 1rem 0;
}
.strong-text {
  font-weight: 400;
}
.certify-box {
  display: flex;
}
.button {
  @include primary-button(var(--dark-brand-color));
  margin-top: 2rem;
}
.disabled-button {
  @include primary-button-disabled();
}
.activity-container {
  width: 86%;
}

/* Customize the label (the checkbox-container) */
.checkbox-container {
  display: block;
  position: relative;
  padding: 1.5rem;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 25px;
  width: 25px;
  background-color: $soft-gray;
}

/* On mouse-over, add a grey background color */
.checkbox-container:hover input ~ .checkmark {
  background-color: $mid-gray;
}

/* When the checkbox is checked, add a blue background */
.checkbox-container input:checked ~ .checkmark {
  background-color: var(--dark-brand-color);
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.checkbox-container .checkmark:after {
  left: 9px;
  top: 5px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

li {
  margin-left: 25px;
  font-family: $base-paragraph-font;
  font-size: 0.9rem;
  color: $base-font-color;
  font-weight: 300;
  line-height: 1.4rem;
}

.light-brand-color-block {
  background-color: var(--light-brand-color);
  padding: 1rem 3rem;
  margin: 0 -3rem 0 -3rem;
}
.additional-resources {
  color: white;
}
</style>

<style lang="scss">
.additional-resources > p {
  color: white;
}
</style>
